/**
 * GOVIA UI V2 — Design tokens.
 *
 * UX-2.1 — actualizado para reflejar la identidad visual oficial de GOVIA
 * (`v2/theme.ts`: fondos #0D111A/#0F1B2E, azul #2563FF, cian #00C2FF, gris
 * neutro #A7B3C7, neutro claro #E6EBF2, Space Grotesk), reemplazando la
 * fuente anterior (`theme/palette.ts`, la paleta índigo/Inter de V1/Fase 1
 * — sigue siendo la fuente de verdad para V1 clásico y, vía composición,
 * para Super Admin, ver `super-admin/theme.ts`; V2 ahora tiene su propia
 * identidad definitiva, igual que Super Admin tiene la suya). Los únicos
 * valores que siguen sin ser "oficiales" por hex propio
 * (`surfaceSubtle`, `attention`, `criticalSurfaceBg`) son variaciones de
 * opacidad sobre colores YA EXISTENTES en `v2/theme.ts` — documentado en
 * cada uno, no son colores arbitrarios.
 */
import { alpha } from '@mui/material/styles'

import { clientSurfaces, clientTheme } from '@/v2/theme'

const palette = clientTheme.palette

// ------------------------------ COLOR --------------------------------- //
// Fuente de verdad: v2/theme.ts (identidad oficial GOVIA Client
// Application, UX-2.1) — nunca theme/palette.ts (eso es V1/Super Admin).
export const color = {
  canvas: clientSurfaces.canvas,
  surface: clientSurfaces.card,
  surfaceElevated: clientSurfaces.cardElevated,
  border: clientSurfaces.border,
  borderStrong: clientSurfaces.borderStrong,

  primary: palette.primary.main,
  primaryLight: palette.primary.light,
  // "Intelligence accent" — el cian oficial GOVIA (#00C2FF), ya usado
  // como `secondary` del tema — se reexporta con el nombre semántico que
  // pide V2, no es un color nuevo.
  intelligenceAccent: palette.secondary.main,

  success: palette.success.main,
  warning: palette.warning.main,
  critical: palette.error.main,
  info: palette.info.main,
  // "Attention" — categoría distinta de "warning" (para etiquetar
  // "REQUIERE ATENCIÓN" sin la connotación de "algo está mal" que sí
  // tiene warning en un formulario). Mismo hex que warning — alias
  // semántico, no un color nuevo.
  attention: palette.warning.main,

  textPrimary: palette.text.primary,
  textSecondary: palette.text.secondary,
  // "Muted" — alias de text.disabled, para texto terciario (metadata,
  // labels de evidencia).
  muted: palette.text.disabled,

  gradientPrimary: clientSurfaces.gradientPrimary,
  gradientSubtle: clientSurfaces.gradientSubtle,

  // NUEVO — única extensión real: un tinte más sutil que `surface` (card),
  // para filas densas de inteligencia (IntelligenceSurface) que no deben
  // leerse como una card. Gris neutro oficial (#A7B3C7) a opacidad baja
  // (0.04) — un peldaño por debajo del hover estándar (0.08) — no un
  // valor inventado.
  surfaceSubtle: alpha(palette.text.secondary, 0.04),
  // NUEVO — fondo de CriticalSurface: rojo de `critical` a opacidad baja,
  // mismo patrón que `gradientSubtle` (tinte de color de marca sobre fondo
  // oscuro), aplicado al color crítico en vez de al primario.
  criticalSurfaceBg: alpha(palette.error.main, 0.08),
} as const

// ---------------------------- TYPOGRAPHY -------------------------------- //
// Mapeo semántico → variantes MUI YA EXISTENTES (theme/typography.ts).
// Ver `typographyStyle` más abajo para la única excepción real (Executive
// Display) y `MetricValue` (primitives/) para la única pieza tipográfica
// previa genuinamente nueva (cifras tabulares).
//
// UX-9 FASE 1 — jerarquía de 5 niveles pedida por el encargo
// ("FOCO EJECUTIVO / SECCIÓN / CONTEXTO / EVIDENCIA / METADATO"), mapeada
// contra lo que YA EXISTE aquí — 4 de los 5 niveles no necesitaban ningún
// token nuevo:
//   FOCO EJECUTIVO → typographyStyle.executiveDisplay (nuevo, ver abajo)
//   SECCIÓN        → typographyVariant.sectionHeadline ('h5', ya existía)
//   CONTEXTO       → typographyVariant.body / bodyDense ('body1'/'body2', ya existían)
//   EVIDENCIA      → typographyVariant.evidenceLabel ('caption', ya existía)
//   METADATO       → typographyVariant.metadata ('caption', ya existía)
export const typographyVariant = {
  display: 'h1',
  executiveHeadline: 'h2',
  sectionHeadline: 'h5',
  body: 'body1',
  bodyDense: 'body2',
  intelligenceLabel: 'overline', // ya es mayúsculas + letter-spacing — "REQUIERE ATENCIÓN"
  metadata: 'caption',
  evidenceLabel: 'caption',
  captionText: 'caption',
} as const

// UX-9 FASE 1 — "Executive Display": el único hueco real de la escala
// (encargo `GOVIA_UX9_PHASE1_DESIGN_SYSTEM_REPORT.md` §7/§10 de
// `GOVIA_UX9_MASTER_PRODUCT_DESIGN_PLAN.md` — H-I1 de UX-8: que el foco
// ejecutivo "domine visualmente" en vez de competir en peso con el resto
// de la pantalla). NO es una nueva variante formal de MUI Typography
// (evita augmentar el tipo global `TypographyVariants`, que afectaría a
// V1/Super Admin también) — es un objeto `sx` aditivo para superponer
// sobre `variant="h1"` (`typographyVariant.display`), reservado para
// UN elemento por pantalla (el titular del bloque de Nivel 1). `h1` ya
// es 2.5rem/700 (`theme/typography.ts`) — este overlay lo distingue con
// un tamaño/interlineado que ningún otro texto de la pantalla alcanza,
// sin tocar la definición de `h1` para el resto de consumidores (V1,
// Super Admin, y cualquier otro `<Typography variant="h1">` de V2 que
// no pase este `sx`).
//
// UX-9 FASE 1.1 — refinamiento tras revisión humana ("PARTIAL": se
// aproximaba a una landing page en vez de sentirse "titular ejecutivo").
// Tres correcciones sobre el valor original (3.25rem/800/-0.03em/1.05):
//   1. `fontWeight: 700` (antes 800) — Space Grotesk solo carga los
//      pesos 400/500/600/700 (`index.html`, `wght@400;500;600;700`); 800
//      forzaba una negrita SINTÉTICA del navegador (el peso real no
//      existe en la fuente cargada), lo que por sí solo ya aportaba a la
//      sensación de "agresivo". 700 es real, coincide con `h1`.
//   2. `fontSize` responsivo (`xs`/`md`) en vez de un único valor fijo
//      3.25rem — a 390px un bloque de 52px de alto competía con toda la
//      pantalla; a 1440px seguía siendo válido mantenerlo por encima de
//      `h1` (2.5rem) pero sin la exageración previa.
//   3. `letterSpacing: '-0.02em'` (antes -0.03em, igual que `h1`) y
//      `lineHeight: 1.15` (antes 1.05) — menos denso, menos "gritado".
// Resultado: sigue siendo, en cualquier ancho, más grande que cualquier
// otro texto de la pantalla (autoridad/presencia), pero ya no reescala
// como una portada — es un titular ejecutivo, no un display de landing.
// Uso: <Typography variant={typographyVariant.display} sx={typographyStyle.executiveDisplay}>
export const typographyStyle = {
  executiveDisplay: {
    fontWeight: 700,
    fontSize: { xs: '2rem', md: '2.75rem' },
    lineHeight: 1.15,
    letterSpacing: '-0.02em',
  },
} as const

// ------------------------------ MOTION ----------------------------------- //
export { motion as motionTokens } from '@/v2/design-system/motion'

// --------------------------- BRAND MOTIF (prototipo) ---------------------- //
// UX-9 FASE 1 — encargo "BRAND MOTIF": prototipar ÚNICAMENTE la capacidad,
// sin aplicarla todavía en ninguna pantalla real. Deliberadamente NO se
// exporta desde `index.ts` (el barrel público de design-system) — solo
// desde este módulo — para que su único consumidor posible por ahora sea
// una validación visual explícita (`DesignSystemPlaygroundPage`), nunca un
// import accidental desde Inicio/Inteligencia/Territorio/Riesgos/etc.
// Patrón abstracto de nodos + líneas (red/conexión/inteligencia — nunca un
// mapa reconocible, nunca un símbolo partidista/político): opacidad
// horneada en el propio SVG (no depende de que el consumidor recuerde
// bajarla), color = gris neutro oficial (`#A7B3C7`, el mismo hex ya usado
// en `color.textSecondary`/`color.muted` — cero color nuevo).
//
// UX-9 FASE 1.1 — refinamiento tras revisión humana (objetivo: sentirse
// "inteligencia/territorio/conexión/señal/red", nunca cyberpunk/neón/
// glitch/mapa reconocible). Dos ajustes sobre el prototipo original:
//   1. Tile más grande (160px, antes 120px) y 6 nodos de radio variable
//      (antes 4 nodos idénticos) conectados de forma irregular hacia un
//      nodo central — se lee como una red orgánica de señales, no como
//      una cuadrícula geométrica repetitiva (que es lo que empezaba a
//      sugerir "gamer/HUD" en el prototipo original).
//   2. Opacidad 0.045 (antes 0.05) — un paso más discreto.
// Uso previsto (si se adopta tras revisión humana): `sx={{ backgroundImage: brandMotif.networkPattern }}`
// sobre un fondo ya oscuro (`color.canvas`/`color.surface`), nunca sobre
// una superficie con datos encima.
export const brandMotif = {
  networkPattern:
    'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'160\' height=\'160\' viewBox=\'0 0 160 160\'%3E%3Cg fill=\'none\' stroke=\'%23A7B3C7\' stroke-width=\'1\' opacity=\'0.045\'%3E%3Cline x1=\'24\' y1=\'28\' x2=\'92\' y2=\'72\'/%3E%3Cline x1=\'92\' y1=\'72\' x2=\'132\' y2=\'22\'/%3E%3Cline x1=\'92\' y1=\'72\' x2=\'34\' y2=\'124\'/%3E%3Cline x1=\'92\' y1=\'72\' x2=\'142\' y2=\'112\'/%3E%3Cline x1=\'24\' y1=\'28\' x2=\'70\' y2=\'46\'/%3E%3Ccircle cx=\'24\' cy=\'28\' r=\'2.5\'/%3E%3Ccircle cx=\'132\' cy=\'22\' r=\'1.5\'/%3E%3Ccircle cx=\'92\' cy=\'72\' r=\'2\'/%3E%3Ccircle cx=\'34\' cy=\'124\' r=\'1.6\'/%3E%3Ccircle cx=\'142\' cy=\'112\' r=\'2.4\'/%3E%3Ccircle cx=\'70\' cy=\'46\' r=\'1.3\'/%3E%3C/g%3E%3C/svg%3E")',
} as const
